import { AiTagUsageInputSchema } from '@erp/rpc';
import { z } from 'zod';

/**
 * The page's URL state, and the rules that keep a hand-edited or stale URL usable.
 *
 * Kept out of the route module so it can be tested without pulling in the authenticated
 * route tree, and so the normalization rules read as one function rather than as a chain of
 * schema modifiers spread across a `createRoute` call.
 */

/** Every collapsible section whose open state is shareable. */
export const AI_USAGE_SECTION_IDS = [
  'people',
  'workflows',
  'agents',
  'product',
  'models',
  'other',
  'technical',
] as const;

export type AiUsageSectionId = (typeof AI_USAGE_SECTION_IDS)[number];

/** Sections expanded when the URL says nothing. Everything else starts collapsed. */
export const DEFAULT_OPEN_SECTIONS: readonly AiUsageSectionId[] = ['people'];

const SECTION_IDS: ReadonlySet<string> = new Set(AI_USAGE_SECTION_IDS);

export function isAiUsageSectionId(value: string): value is AiUsageSectionId {
  return SECTION_IDS.has(value);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export interface AiUsageSearch {
  startDate?: string;
  endDate?: string;
  /** The workflow family whose versions are expanded. */
  workflowId?: string;
  /** Sections the viewer opened or closed away from the default. */
  sections?: AiUsageSectionId[];
}

/**
 * Raw, per-field-tolerant parse. Every field falls back rather than erroring, because a bad
 * value in one of them must not throw away the rest: a shared link with a typo'd date should
 * still land on the right workflow.
 */
const rawSearchSchema = z.object({
  startDate: z.string().regex(DATE_ONLY).optional().catch(undefined),
  endDate: z.string().regex(DATE_ONLY).optional().catch(undefined),
  workflowId: z.string().uuid().optional().catch(undefined),
  // Parsed as loose strings and filtered below rather than as `z.enum`, so one unrecognized
  // id — a section this build no longer has — does not discard the viewer's other choices.
  sections: z.array(z.string()).optional().catch(undefined),
});

/**
 * Drops what cannot be honored and keeps what can.
 *
 * The date pair is validated as a PAIR against the RPC input's own rules (reversed, or wider
 * than the server accepts), reusing that schema so the route and the server cannot drift.
 * When the pair fails, only the dates are dropped — the window falls back to the default
 * while `workflowId` and the open sections survive.
 */
export function normalizeAiUsageSearch(raw: unknown): AiUsageSearch {
  const parsed = rawSearchSchema.safeParse(raw);
  if (!parsed.success) return {};

  const { startDate, endDate, workflowId, sections } = parsed.data;
  const range = AiTagUsageInputSchema.safeParse({ startDate, endDate }).success
    ? { startDate, endDate }
    : { startDate: undefined, endDate: undefined };
  return {
    ...range,
    workflowId,
    // An empty array SURVIVES, and is not folded back to `undefined`: absent means "use the
    // defaults", while empty means the viewer collapsed everything. Collapsing them would
    // make closing the one section that opens by default reopen it on the next render.
    sections: sections?.filter(isAiUsageSectionId),
  };
}
